import React from "react";
import { Box, MenuItem, TextField, Typography } from "@mui/material";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import BalanceOutlinedIcon from "@mui/icons-material/BalanceOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import ModelCardPicker from "../../../common/ModelCardPicker";
import AgentBaseCostBadge from "../../../common/AgentBaseCostBadge";
import AgentConfigSection from "../AgentConfigSection";
import {
  REASONING_EFFORT_DEFAULT,
  REASONING_EFFORT_OPTIONS,
  supportsReasoningEffort,
} from "../../../../utils/ModelConfig";

const SECTION_CARD_SX = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "8px",
  p: 2,
  backgroundColor: "action.hover",
};

const REASONING_EFFORT_HINTS = {
  LOW: "Faster responses with a smaller reasoning budget.",
  MEDIUM: "Balanced reasoning for everyday agent tasks.",
  HIGH: "More deliberate reasoning for complex, multi-step work.",
};

const REASONING_EFFORT_ICONS = {
  LOW: BoltOutlinedIcon,
  MEDIUM: BalanceOutlinedIcon,
  HIGH: PsychologyOutlinedIcon,
};

const FIELD_WIDTH_SX = { maxWidth: 280 };

function ReasoningEffortOptionLabel({ value, label }) {
  const Icon = REASONING_EFFORT_ICONS[value];
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {Icon ? (
        <Icon sx={{ fontSize: 16, color: "text.secondary" }} />
      ) : null}
      <span>{label}</span>
    </Box>
  );
}

function getReasoningEffortLabel(value) {
  return (
    REASONING_EFFORT_OPTIONS.find((option) => option.value === value)?.label ||
    value
  );
}

export default function ModelTab({
  isSystemAgent,
  model,
  setModel,
  reasoningEffort,
  setReasoningEffort,
  maxIterations,
  setMaxIterations,
  agentBaseCost,
}) {
  const hasReasoningEffort = supportsReasoningEffort(model);
  const selectedReasoningEffort = reasoningEffort || REASONING_EFFORT_DEFAULT;

  return (
    <>
      {isSystemAgent && (
        <Box
          sx={{
            mb: 2.5,
            p: 1.5,
            borderRadius: "8px",
            backgroundColor: "action.hover",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
            This is a system agent. Only the model can be changed.
          </Typography>
        </Box>
      )}

      <AgentConfigSection id="modelSelection">
        <Typography className="org-field-label">Model</Typography>
        <ModelCardPicker
          className="org-styled-textfield"
          value={model}
          onChange={(nextModel) => {
            setModel(nextModel);
            if (!supportsReasoningEffort(nextModel)) {
              setReasoningEffort(REASONING_EFFORT_DEFAULT);
            }
          }}
        />
        {agentBaseCost ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 1.25,
              flexWrap: "wrap",
            }}
          >
            <AgentBaseCostBadge
              tier={agentBaseCost.tier}
              rawTokens={agentBaseCost.rawTokens}
            />
            <Typography sx={{ fontSize: "12px", color: "text.disabled" }}>
              Standing context this agent sends every turn (prompt, tools,
              knowledge). Updates as you configure it.
            </Typography>
          </Box>
        ) : null}
      </AgentConfigSection>

      <AgentConfigSection id="reasoning" sx={{ mt: 2.5 }}>
        <Box sx={SECTION_CARD_SX}>
          {hasReasoningEffort ? (
            <>
              <Typography className="org-field-label">Reasoning effort</Typography>
              <TextField
                className="org-styled-textfield"
                select
                fullWidth
                size="small"
                value={selectedReasoningEffort}
                onChange={(e) => setReasoningEffort(e.target.value)}
                sx={FIELD_WIDTH_SX}
                SelectProps={{
                  renderValue: (value) => (
                    <ReasoningEffortOptionLabel
                      value={value}
                      label={getReasoningEffortLabel(value)}
                    />
                  ),
                }}
              >
                {REASONING_EFFORT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <ReasoningEffortOptionLabel
                      value={option.value}
                      label={option.label}
                    />
                  </MenuItem>
                ))}
              </TextField>
              <Typography
                sx={{
                  fontSize: "13px",
                  color: "text.secondary",
                  lineHeight: 1.5,
                  mt: 1,
                  maxWidth: 520,
                }}
              >
                {REASONING_EFFORT_HINTS[selectedReasoningEffort] ||
                  REASONING_EFFORT_HINTS[REASONING_EFFORT_DEFAULT]}
              </Typography>
            </>
          ) : (
            <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
              Reasoning effort is not available for the selected model.
            </Typography>
          )}
        </Box>
      </AgentConfigSection>

      {!isSystemAgent && (
        <AgentConfigSection id="runtimeLimits" sx={{ mt: 2.5 }}>
          <Box sx={SECTION_CARD_SX}>
            <Typography className="org-field-label">Max iterations</Typography>
            <TextField
              className="org-styled-textfield"
              type="number"
              fullWidth
              size="small"
              value={maxIterations}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val) && val >= 1 && val <= 200) {
                  setMaxIterations(val);
                }
              }}
              inputProps={{ min: 1, max: 200 }}
              sx={FIELD_WIDTH_SX}
            />
            <Typography
              sx={{
                fontSize: "13px",
                color: "text.secondary",
                lineHeight: 1.5,
                mt: 1,
                maxWidth: 520,
              }}
            >
              Each iteration is one model turn in the agent loop. The agent keeps
              working until it finishes without calling tools or hits this limit.
            </Typography>
          </Box>
        </AgentConfigSection>
      )}
    </>
  );
}
